export { cn, formatPaise } from "./lib/cn";

export { Button, buttonVariants, type ButtonProps } from "./components/Button";
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "./components/Card";
export { Input, type InputProps } from "./components/Input";
export { Label } from "./components/Label";
export { Select, type SelectProps } from "./components/Select";
export { Badge, badgeVariants, type BadgeProps } from "./components/Badge";
export {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./components/Table";
export {
  Spinner,
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  DataTableSkeleton,
  CatalogGridSkeleton,
  StatCardsSkeleton,
  ProgressBar,
} from "./components/Feedback";
export {
  InteractiveLoader,
  PageLoadingScreen,
  MANDI_FACTS,
  type MandiFact,
  type InteractiveLoaderProps,
} from "./components/InteractiveLoader";
export { EmptyState, ErrorState } from "./components/States";
export { ErrorBoundary, type ErrorBoundaryProps } from "./components/ErrorBoundary";
export { ToastProvider, useToast } from "./components/Toast";
export { ThemeProvider, ThemeToggle, useTheme } from "./components/Theme";
export { AppShell, PageHeader, scrollToTop, type NavItem, type AppShellProps } from "./components/AppShell";
export { LoginScreen, type LoginScreenProps, type LoginValues } from "./components/LoginScreen";
export { ResilientImage } from "./components/ResilientImage";
export { DocumentUpload, type DocumentUploadProps } from "./components/DocumentUpload";
export { MultiImageUpload, compressAndReadImage, type MultiImageUploadProps } from "./components/MultiImageUpload";
export { ImageLightboxModal, type ImageLightboxModalProps } from "./components/ImageLightboxModal";

// Phase 1B Components
export { Dialog, DialogOverlay, DialogContent, DialogHeader, DialogFooter, DialogClose } from './components/Dialog';
export { Tabs, TabsList, TabsTrigger, TabsContent } from './components/Tabs';
export { StatCard, type StatCardProps } from './components/StatCard';
export { SearchBar, type SearchBarProps } from './components/SearchBar';
export { DropdownMenu, DropdownTrigger, DropdownContent, DropdownItem, DropdownSeparator } from './components/DropdownMenu';
export { Breadcrumbs, type BreadcrumbsProps } from './components/Breadcrumbs';
export { FilterBar, type FilterBarProps } from './components/FilterBar';
export { ProgressSteps, type ProgressStepsProps } from './components/ProgressSteps';

// Feature Flag for Credit Khata / Trade Credit
export const SHOW_CREDIT_KHATA = false;

// Phase 2 Components
export { SlideToConfirm, type SlideToConfirmProps } from './components/SlideToConfirm';
export { CreditKhataCard, type CreditKhataCardProps } from './components/CreditKhataCard';
export { MarginIndicator, type MarginIndicatorProps } from './components/MarginIndicator';
export { RepeatOrderRail, type RepeatOrderRailProps, type RepeatOrderItem } from './components/RepeatOrderRail';
export { OfflineSyncBadge, type OfflineSyncBadgeProps } from './components/OfflineSyncBadge';
export { InspectionDrawer, type InspectionDrawerProps } from './components/InspectionDrawer';
export { BulkActionBar, type BulkActionBarProps } from './components/BulkActionBar';
export { VehicleUtilizationBar, type VehicleUtilizationBarProps } from './components/VehicleUtilizationBar';
export { CommandPalette, type CommandPaletteProps, type CommandItem } from './components/CommandPalette';
export { QuickPunchGrid, type QuickPunchGridProps, type Sku, type OrderLine } from './components/QuickPunchGrid';
export { CashCounterUtility, type CashCounterUtilityProps } from './components/CashCounterUtility';
export { MyDailyDirectLogo, TradoLogo, type MyDailyDirectLogoProps, type TradoLogoProps } from './components/MyDailyDirectLogo';
export { TaxInvoiceModal, type TaxInvoiceModalProps, type InvoiceLineItem } from './components/TaxInvoiceModal';
export { QRCodeView, type QRCodeViewProps } from './components/QRCodeView';

// Phase 3 Components
export { ExcelCatalogUploader, type ExcelCatalogUploaderProps } from './components/ExcelCatalogUploader';
export {
  exportCatalogToXlsx,
  generateTemplateXlsx,
  parseCatalogXlsx,
  validateParsedRows,
  downloadBlob,
  inferWeightKg,
  calculateSellingPricePaise,
  calculateNipPaise,
  calculateMrpFromMarginAndGst,
  calculateMarginFromMrpAndGst,
  getDefaultMddMarginPerKg,
  type ParsedSkuRow,
  type ValidationResult,
  type SkuExportRow,
} from './lib/catalogExcelUtils';

// KYC & Document Proof Helpers
export {
  parseRetailerKyc,
  serializeRetailerKyc,
  openPdfInNewTab,
  SUPPORTED_LANGUAGES,
  type SupportedLanguageCode,
  getLanguageLabel,
  getLanguageShortLabel,
  type RetailerKycPhotos,
} from './lib/kyc';

// Communications & Tracking Store
export {
  recordCallCommunication,
  recordWhatsAppCommunication,
  getCommunicationsForRetailer,
  getAllCommunications,
  getCommunicationStats,
  subscribeToCommunications,
  type CommunicationRecord,
  type CommunicationType,
  type CommunicationRole,
  type CallOutcome,
  type CommunicationStats,
} from './lib/communications';

export {
  CallRetailerModal,
  type CallRetailerModalProps,
} from './components/CallRetailerModal';

// Growth & Buyer-Reach Components
export {
  WhatsAppMessageModal,
  OFFICIAL_WHATSAPP_TEMPLATES,
  generateOfficialWhatsAppTemplate,
  type WhatsAppMessageModalProps,
  type WhatsAppOrderDetails,
  type WhatsAppOrderItem,
  type WhatsAppRetailerDetails,
  type WhatsAppTemplateKey,
} from './components/WhatsAppMessageModal';
export {
  MandiRateCardModal,
  type MandiRateCardModalProps,
  type MandiRateSku,
} from './components/MandiRateCardModal';
export {
  SavingsCalculatorModal,
  type SavingsCalculatorModalProps,
  type ComparisonItem,
} from './components/SavingsCalculatorModal';
export {
  SampleKitModal,
  type SampleKitModalProps,
  type SampleKitRequest,
} from './components/SampleKitModal';
export {
  getVolumeTiers,
  calculateEffectiveTierPrice,
  hasActiveQpsScheme,
  type VolumeTier,
  type QpsConfig,
} from './lib/volumePricing';

// MDD Canonical Support & Registration Constants
export {
  MDD_SUPPORT_PHONE,
  MDD_SUPPORT_PHONE_DISPLAY,
  MDD_SUPPORT_WHATSAPP_NUMBER,
  MDD_SUPPORT_WHATSAPP_URL,
  MDD_SUPPORT_TEL_URL,
  MDD_SUPPORT_EMAIL,
  MDD_GSTIN,
  MDD_PAN,
  MDD_TRADE_NAME,
  MDD_CONSTITUTION,
  MDD_GST_STATE,
  MDD_GST_STATE_CODE,
  MDD_GST_REGISTRATION_DATE,
  MDD_GST_REGISTRATION_TYPE,
  MDD_REGISTERED_ADDRESS,
} from './constants/contact';

// Design System v2 Components & Direction Helpers (Redesign Experiment)
export { Banner, IconTile, toneSoft, type BannerProps, type IconTileProps, type Tone } from './components/Banner';
export { FilterChip, type FilterChipProps } from './components/Chip';
export { PriceTag, type PriceTagProps } from './components/PriceTag';
export { QuantityStepper, type QuantityStepperProps } from './components/QuantityStepper';
export { ProductCard, type ProductCardProps, type ProductCardSku } from './components/ProductCard';
export { StickyActionBar, type StickyActionBarProps } from './components/StickyActionBar';
export {
  DESIGN_DIRECTIONS,
  applyDesignDirection,
  readDesignDirection,
  useDesignDirection,
  type DesignDirection,
  type DesignDirectionMeta,
} from './lib/designDirection';

// Assortments / Pack Sizes Components & Helpers
export {
  AssortmentEditor,
  type AssortmentEditorProps,
} from './components/AssortmentEditor';
export {
  ASSORTMENT_PRESETS,
  PACKAGING_OPTIONS,
  type PackagingOptionValue,
  PACK_UNITS,
  inferPackagingType,
  buildPackLabel,
  packWeightKg,
  activePacks,
  hasAssortments,
  defaultPack,
  resolvePack,
  unitsPerPack,
  packView,
  minPackPricePaise,
  cartLineKey,
  parseCartLineKey,
  type AssortmentPack,
  type AssortmentSkuLike,
  type AssortmentPreset,
} from './lib/assortments';

// Vertical-Level Baseline Margin & Logistics Pricing Engine
export {
  DEFAULT_VERTICAL_PRICING_RULES,
  loadLocalPricingRules,
  saveLocalPricingRules,
  isVolumeMeasurement,
  resolvePricingVertical,
  calculateVerticalBaseline,
  computeVerticalPricing,
  type PricingVerticalCode,
  type BaselinePricingCalcResult,
} from './lib/verticalPricingEngine';


